import React, { ReactElement, useState, useEffect } from 'react';
import { DI, DIProps } from '../../../../../Core';
import { InlineError, ContextualSaveBar, BlockStack, Grid, Text, Divider, InlineStack, Icon, TextField, Box, Modal } from '@shopify/polaris';
import { Switch } from 'antd';
import useContextualSaveBar, { isAdminLoggedIn } from '../../../Panel/index';
import { RefreshMajor } from '@shopify/polaris-icons';
import { getLastSyncInformation } from '../../ConfigHelper';
import { RenderSkeletonProductDetails } from '../../skeleton/skeleton';
import Draggable from './Draggable';
import { useTranslation } from 'react-i18next';
import { environment } from '../../../../../environments/environment';

interface Props extends DIProps {
    defaultAttributes: any;
    data: any;
    syncNecessaryInfo: () => void;
    syncConnectorInfo: () => void;
}

interface syncStatusWithInterface {
    value: boolean
    sku: boolean
    barcode: boolean
    priority: {
        "0": "sku" | "barcode",
        "1": "sku" | "barcode"
    }
}

interface syncProductWithInterface {
    FBA: syncStatusWithInterface
    FBM: syncStatusWithInterface
}

interface productSettingsInterface {
    "data_type": string,
    "settings_enabled": boolean,
    "images": boolean,
    "variant_title": boolean,
    "product_details": boolean,
    "selected_attributes": string[],
    "sync_product_with": syncProductWithInterface,
    "trim_description": boolean,
    "sync_feed_process": boolean
}

export const defaultProductSettings: productSettingsInterface = {
    "data_type": "product_settings",
    "settings_enabled": true,
    "images": false,
    "variant_title": true,
    "product_details": false,
    "selected_attributes": [],
    "sync_product_with": {
        FBA: {
            value: false,
            sku: false,
            barcode: false,
            priority: ["sku", "barcode"]
        },
        FBM: {
            value: false,
            sku: false,
            barcode: false,
            priority: ["sku", "barcode"]
        },
    },
    "trim_description": true,
    "sync_feed_process": false
};

function ProductSettings(props: Props): ReactElement {
    const { t } = useTranslation();
    const [metafield, setMetaField] = useState<boolean>(false);
    const [savedMetafield, setSavedMetaField] = useState<boolean>(false);
    const [metaFieldModal, setMetaFieldModal] = useState<boolean>(false);
    const [shouldMetaAttributesDisabled, setShouldMetaAttributesDisabled] = useState<boolean>(false);
    const { showSaveBar, hideSaveBar } = useContextualSaveBar(props);
    const [savedProductSettings, setSavedProductSettings] = useState<productSettingsInterface>(JSON.parse(JSON.stringify(defaultProductSettings)));
    const [productSettings, setProductSettings] = useState<productSettingsInterface>(JSON.parse(JSON.stringify(defaultProductSettings)));
    const [errors, setErrors] = useState<any>({
        fba: false,
        fbm: false
    });
    const [fullfillmentError, setFullfillmentError] = useState<boolean>(false);
    const [pageSkeleton, setPageSkeleton] = useState<boolean>(true);
    const [showContextualSaveBar, setShowContextualSaveBar] = useState<boolean>(false);
    const [saveLoader, setSaveLoader] = useState<boolean>(false);
    const [getLoader, setGetLoader] = useState<boolean>(false);
    const [lastSync, setLastSync] = useState('');
    const [isSyncStatusKeyExist, setSyncStatusExist] = useState<boolean>(false);
    const [inventory_fulfillment_latency, setInventoryFullfilmentLatency] = useState({
        error: false,
        value: '',
        saved: ''
    })
    const [fba_priority, setFbaPriority] = useState<any>({
        value: [],
        saved: []
    });
    const [fbm_priority, setFbmPriority] = useState<any>({
        value: [],
        saved: []
    });

    function compareStringValidator(oldValue: any, newValue: any) {
        let flag: boolean = true;
        if (oldValue?.length === newValue?.length) {
            oldValue?.map((event: any) => {
                if (flag) {
                    flag = newValue.includes(event);
                }
            })
            return flag
        } else {
            return !flag;
        }
    }

    const getLastProductSyncDate = () => {
        let data = getLastSyncInformation(props, 'product');
        data.then((res: any) => {
            if (res?.error) props.error(t(res?.error) ?? '');
            else setLastSync(res?.date ?? '')
        })
    }

    const onProductSettingsChange = (key: string, subKey: string, value: boolean, subKey2?: string) => {
        let tempSettings: any = JSON.parse(JSON.stringify(productSettings));
        if (subKey2 && subKey2 !== "") {
            tempSettings[key][subKey][subKey2] = value;
        }
        else {
            if (subKey != "") {
                tempSettings[key][subKey] = value;
            }
            else {
                tempSettings[key] = value;
                if (key != "metafield" && key != "trim_description" && key != "variant_title" && key != "sync_feed_process") {
                    if (value) {
                        tempSettings?.['selected_attributes']?.push(key);
                    }
                    else {
                        tempSettings?.['selected_attributes']?.splice(tempSettings['selected_attributes']?.indexOf(key), 1);
                    }
                }
            }
        }


        setProductSettings(JSON.parse(JSON.stringify(tempSettings)));

        if ((tempSettings?.selected_attributes?.length === savedProductSettings?.selected_attributes?.length) && compareStringValidator(savedProductSettings?.selected_attributes, tempSettings?.selected_attributes)) {
            tempSettings['selected_attributes'] = savedProductSettings?.selected_attributes;
        }

        if (JSON.stringify(tempSettings) != JSON.stringify(savedProductSettings)) setShowContextualSaveBar(true);
        else setShowContextualSaveBar(false);
    }

    const deleteKeyFromConfig = (key: string[]) => {
        props.di.POST('connector/config/deleteConfig', {
            data: [{
                key: key,
                group_code: "product"
            }]
        })
            .then((res: any) => {
                if (res?.success) {

                }
                else props.error(t(res?.message))
            })
    }

    const getPlanStatus = () => {
        let isPaidPlan: boolean = false;
        props.redux.current_plan?.plan_details?.services_groups?.map((service_grp: any) => {
            if (service_grp?.title == "Order Management") {
                service_grp?.services?.map((service: any) => {
                    if (service?.title == "Order Sync") {
                        if (+service?.prepaid?.service_credits > 50) {
                            isPaidPlan = true;
                        }
                    }
                })
            }
        });
        return isPaidPlan;
    }

    const metaFieldSupportAllowed = () => {
        //meta field support from services
        let allowed: boolean = false;
        if (props?.redux?.additional_services?.supported_features?.metafield_support) {
            allowed = true;
        }

        // allow meta field support for paid plans
        // props.redux.current_plan?.plan_details?.services_groups?.map((service_grp: any) => {
        //     if (service_grp?.title == "Order Management") {
        //         service_grp?.services?.map((service: any) => {
        //             if (service?.title == "Order Sync") {
        //                 if (+service?.prepaid?.service_credits > 50) {
        //                     allowed = true;
        //                 }
        //             }
        //         })
        //     }
        // });

        return allowed;
    }

    const getProductSettings = async (from: string = "get") => {
        let tempProdSettings = from == "discard" ? JSON.parse(JSON.stringify(defaultProductSettings)) : JSON.parse(JSON.stringify(productSettings));
        let { success, data, message } = await props.di.POST('connector/config/getConfig', {
            "group_code": ["product", "inventory"]
        });
        const inventory_settings = data?.find((a: any) => a.group_code === "inventory");
        if (success) {
            if (inventory_settings
                && Object.keys(inventory_settings?.['value'])?.length > 0
                && inventory_settings?.value?.inventory_fulfillment_latency) {
                setInventoryFullfilmentLatency({ error: false, value: inventory_settings?.value?.inventory_fulfillment_latency ?? '', saved: inventory_settings?.value?.inventory_fulfillment_latency ?? '' })
            }

            if (data?.[0]?.['group_code'] == "product" && data?.[0]?.['value']) {

                let dataValue = data?.[0]?.['value'];
                if (dataValue?.hasOwnProperty('settings_enabled')) tempProdSettings['settings_enabled'] = dataValue?.settings_enabled
                if (dataValue?.hasOwnProperty('images')) tempProdSettings['images'] = dataValue?.images
                if (dataValue?.hasOwnProperty('variant_title')) tempProdSettings['variant_title'] = dataValue?.variant_title
                if (dataValue?.hasOwnProperty('trim_description')) tempProdSettings['trim_description'] = dataValue?.trim_description
                if (dataValue?.hasOwnProperty('product_details')) tempProdSettings['product_details'] = dataValue?.product_details
                if (dataValue?.hasOwnProperty('selected_attributes')) tempProdSettings['selected_attributes'] = dataValue?.selected_attributes
                if (dataValue?.hasOwnProperty('sync_feed_process')) tempProdSettings['sync_feed_process'] = dataValue?.sync_feed_process

                if (dataValue?.hasOwnProperty('sync_status_with') && dataValue?.sync_status_with) {
                    setSyncStatusExist(true)
                }
                if (dataValue?.hasOwnProperty('sync_product_with') && dataValue?.sync_product_with) {
                    if (dataValue?.sync_product_with.constructor.name === "Object" && Object.keys(dataValue?.sync_product_with).length > 0) {    //kbhi kbhi obj ki jagh array aa rhi
                        tempProdSettings['sync_product_with'] = dataValue?.sync_product_with;
                        if (dataValue?.sync_product_with?.['FBA']) {
                            setFbaPriority({ ...fba_priority, value: dataValue?.sync_product_with?.['FBA']?.['priority'], saved: dataValue?.sync_product_with?.['FBA']?.['priority'] });
                        }
                        else {
                            tempProdSettings['sync_product_with'] = {
                                "FBA": {
                                    value: dataValue?.sync_product_with?.['fba'] || true,
                                    sku: dataValue?.sync_status_with?.['sku'] || true,
                                    barcode: dataValue?.sync_status_with?.['barcode'] || false,
                                    priority: ["sku", "barcode"]
                                }
                                ,
                                "FBM": {
                                    value: dataValue?.sync_product_with?.['fbm'] || false,
                                    sku: dataValue?.sync_status_with?.['sku'] || true,
                                    barcode: dataValue?.sync_status_with?.['barcode'] || false,
                                    priority: ["sku", "barcode"]
                                }
                            }
                            setFbaPriority({ ...fba_priority, value: tempProdSettings?.sync_product_with?.['FBA']?.['priority'], saved: tempProdSettings?.sync_product_with?.['FBA']?.['priority'] })
                            setFbmPriority({ ...fba_priority, value: tempProdSettings?.sync_product_with?.['FBA']?.['priority'], saved: tempProdSettings?.sync_product_with?.['FBA']?.['priority'] })
                        }
                        if (dataValue?.sync_product_with?.['FBM']) {
                            setFbmPriority({ ...fbm_priority, value: dataValue?.sync_product_with?.['FBM']?.['priority'], saved: dataValue?.sync_product_with?.['FBM']?.['priority'] });
                        }
                        else {
                            tempProdSettings['sync_product_with'] = {
                                "FBA": {
                                    value: dataValue?.sync_product_with?.['fba'] || true,
                                    sku: dataValue?.sync_status_with?.['sku'] || true,
                                    barcode: dataValue?.sync_status_with?.['barcode'] || false,
                                    priority: ["sku", "barcode"]
                                },
                                "FBM": {
                                    value: dataValue?.sync_product_with?.['fbm'] || false,
                                    sku: dataValue?.sync_status_with?.['sku'] || true,
                                    barcode: dataValue?.sync_status_with?.['barcode'] || false,
                                    priority: ["sku", "barcode"]
                                }
                            }
                            setFbaPriority({ ...fba_priority, value: tempProdSettings?.sync_product_with?.['FBA']?.['priority'], saved: tempProdSettings?.sync_product_with?.['FBA']?.['priority'] })
                            setFbmPriority({ ...fba_priority, value: tempProdSettings?.sync_product_with?.['FBA']?.['priority'], saved: tempProdSettings?.sync_product_with?.['FBA']?.['priority'] })
                        }
                    }
                    else if (Array.isArray(dataValue?.sync_product_with) && dataValue?.sync_product_with?.length > 0) {
                        dataValue?.sync_product_with?.map((key: any) => {
                            if (key == "FBA") {
                                tempProdSettings['sync_product_with']['FBA'] = dataValue['sync_product_with']['FBA'];
                            }
                            else if (key == "FBM") {
                                tempProdSettings['sync_product_with']['FBM'] = dataValue['sync_product_with']['FBA'];
                            }
                            dataValue?.sync_product_with?.['FBA'] && setFbaPriority({ ...fba_priority, value: dataValue?.sync_product_with?.['FBA']?.['priority'] });
                            dataValue?.sync_product_with?.['FBM'] && setFbmPriority({ ...fbm_priority, value: dataValue?.sync_product_with?.['FBM']?.['priority'] });
                        })
                    }
                }
                else {
                    tempProdSettings['sync_product_with'] = {
                        "FBA": {
                            value: true,
                            sku: true,
                            barcode: false,
                            priority: ["sku", "barcode"]
                        },
                        "FBM": {
                            value: false,
                            sku: true,
                            barcode: false,
                            priority: ["sku", "barcode"]
                        }
                    }
                    setFbaPriority({ ...fba_priority, value: tempProdSettings?.sync_product_with?.['FBA']?.['priority'], saved: tempProdSettings?.sync_product_with?.['FBA']?.['priority'] })
                    setFbmPriority({ ...fba_priority, value: tempProdSettings?.sync_product_with?.['FBA']?.['priority'], saved: tempProdSettings?.sync_product_with?.['FBA']?.['priority'] })
                }
                // if (getPlanStatus() && +props.redux.current_plan?.plan_details?.custom_price > 0) { //paid plan
                // if (metaFieldSupportAllowed()) { //paid plan with meta field support ON
                //     if (dataValue?.hasOwnProperty('metafield')) {
                //         tempProdSettings['metafield'] = dataValue?.metafield;
                //     }
                // }
                // else { //free plan
                //     // setShouldMetaAttributesDisabled(true);
                //     tempProdSettings['metafield'] = false;
                // }

            }
            else {
                tempProdSettings['sync_product_with'] = {
                    "FBA": {
                        value: true,
                        sku: true,
                        barcode: false,
                        priority: ["sku", "barcode"]
                    },
                    "FBM": {
                        value: false,
                        sku: true,
                        barcode: false,
                        priority: ["sku", "barcode"]
                    }
                }
                setFbaPriority({ ...fba_priority, value: tempProdSettings?.sync_product_with?.['FBA']?.['priority'], saved: tempProdSettings?.sync_product_with?.['FBA']?.['priority'] })
                setFbmPriority({ ...fba_priority, value: tempProdSettings?.sync_product_with?.['FBA']?.['priority'], saved: tempProdSettings?.sync_product_with?.['FBA']?.['priority'] })
            }

            setSavedProductSettings(JSON.parse(JSON.stringify(tempProdSettings)))
            setProductSettings(JSON.parse(JSON.stringify(tempProdSettings)))
        }
        // else props.error(t("Oops! Some error occured while getting product settings."));
        setGetLoader(false);
        setPageSkeleton(false);
    }

    const handleDiscardBtn = () => {
        setErrors({
            fba: false,
            fbm: false
        })
        setSyncStatusExist(false)
        setFullfillmentError(false);
        setPageSkeleton(true);
        setShowContextualSaveBar(false);
        setProductSettings(JSON.parse(JSON.stringify(defaultProductSettings)));
        setGetLoader(true);
        getProductSettings("discard");
        getLastProductSyncDate();
    }

    const filterEditedData = (original: any, changed: any) => {
        const allSettings = Object.keys(defaultProductSettings);
        const myObj: any = {};
        allSettings.forEach((setting: string) => {
            if (JSON.stringify(changed[setting]) != JSON.stringify(original[setting])) {
                myObj[setting] = changed[setting];
            }
        });
        return myObj;
    }

    const prepareBackendData = () => {
        const editedData = filterEditedData(savedProductSettings, productSettings);
        let preparedData = JSON.parse(JSON.stringify(editedData));
        let backendData = [
            {
                data: preparedData,
                group_code: "product"
            },
            {
                data: { inventory_fulfillment_latency: inventory_fulfillment_latency.value !== "" ? inventory_fulfillment_latency.value : "2" },
                group_code: "inventory"
            }
        ];
        return backendData;
    }

    const saveProductSettings = async () => {
        setSaveLoader(true);
        if (isSyncStatusKeyExist) {
            deleteKeyFromConfig(['sync_status_with'])
        }
        if (!errors?.fba && !errors?.fbm && !fullfillmentError && !inventory_fulfillment_latency.error) {
            let backendData = prepareBackendData();
            await props.di.POST('connector/config/saveConfig', {
                "data": backendData
            }).then((response: any) => {
                setShowContextualSaveBar(false);
                setSaveLoader(false);
                if (response?.success) {
                    props.success(t("Product settings saved successfully."));
                    setGetLoader(true);
                    getProductSettings();
                    getLastProductSyncDate();
                }
                else {  //api call mein agar koi error hogi
                    props.error(t("Oops! Some error occured while saving."));
                }
            });

            if (JSON.stringify(savedMetafield) !== JSON.stringify(metafield)) {
                const url = `${environment.API_ENDPOINT}connector/config/saveConfig`;
                const requestHeaders: HeadersInit = new Headers();
                const auth: any = props.di.globalState.getBearerToken();

                requestHeaders.set('Authorization', auth);
                requestHeaders.set('appCode', "eyJzaG9waWZ5IjoiYW1hem9uX3NhbGVzX2NoYW5uZWwiLCJhbWF6b24iOiJhbWF6b24ifQ==");
                requestHeaders.set('appTag', "amazon_sales_channel");
                requestHeaders.set('Content-Security-Policy', "frame-ancestors https://admin.shopify.com");
                requestHeaders.set('Ced-Source-Id', props.di.globalState.get('source_id') ?? '');
                requestHeaders.set('Ced-Source-Name', "shopify");
                requestHeaders.set('ced-target-name', "amazon");
                requestHeaders.set('Content-Type', 'application/json'); // Set the content type to JSON

                // Define the request body with group_code
                const requestBody = JSON.stringify({
                    data: [{
                        group_code: "product", data: {
                            metafield: metafield
                        }
                    }]
                });

                await fetch(url, {
                    headers: requestHeaders,
                    method: 'POST', // Changed to POST method
                    body: requestBody, // Included the request body
                })
                    .then((response) => response.json())
                    .then((e) => {
                        if (e?.success) {
                            setShowContextualSaveBar(false);
                            props.success(t("Metafield data saved successfully."));
                            getMetaFieldValue();
                        }
                        else {  //api call mein agar koi error hogi
                            props.error(t("Oops! Some error occured while saving metafield data."));
                        }
                    })
                    .catch((error) => {
                        // console.error('Error:', error);
                    });
            }

            if (metafield && JSON.stringify(savedMetafield) != JSON.stringify(metafield)) {
                await props.di.POST('shopifyhome/request/metafieldSyncSingleUser', {
                    user_id: props.di.user_id,
                    value: metafield
                }).then((response: any) => {
                    if (response?.success) {
                        props.success(t("Metafield process initiated successfully."));
                    }
                    else {  //api call mein agar koi error hogi
                        props.error(t("Oops! Some error occured while initiating metafield process."));
                    }
                })
            }
        }
        else {
            props.error(t("Some information is missing or wrong, kindly check them before proceeding."));
            setSaveLoader(false);
        }
    }

    if (process.env.REACT_APP_PROD === "1" && !isAdminLoggedIn()) {
        let savebar_options: any = {
            loading: saveLoader,
        };

        if (showContextualSaveBar || (JSON.stringify(savedMetafield) !== JSON.stringify(metafield)) || inventory_fulfillment_latency.saved !== inventory_fulfillment_latency.value || JSON.stringify(fba_priority?.value) !== JSON.stringify(fba_priority?.saved) || JSON.stringify(fbm_priority?.value) !== JSON.stringify(fbm_priority?.saved)) {
            showSaveBar(handleDiscardBtn, saveProductSettings, { ...savebar_options });
        } else hideSaveBar();
    }

    const getMetaFieldValue = async () => {
        if (metaFieldSupportAllowed()) {
            const url = `${environment.API_ENDPOINT}connector/config/getConfig`;
            const requestHeaders: HeadersInit = new Headers();
            const auth: any = props.di.globalState.getBearerToken();

            requestHeaders.set('Authorization', auth);
            requestHeaders.set('appCode', "eyJzaG9waWZ5IjoiYW1hem9uX3NhbGVzX2NoYW5uZWwiLCJhbWF6b24iOiJhbWF6b24ifQ==");
            requestHeaders.set('appTag', "amazon_sales_channel");
            requestHeaders.set('Content-Security-Policy', "frame-ancestors https://admin.shopify.com");
            requestHeaders.set('Ced-Source-Id', props.di.globalState.get('source_id') ?? '');
            requestHeaders.set('Ced-Source-Name', "shopify");
            requestHeaders.set('ced-target-name', "amazon");
            requestHeaders.set('Content-Type', 'application/json'); // Set the content type to JSON

            // Define the request body with group_code
            const requestBody = JSON.stringify({
                group_code: ["product"]
            });

            await fetch(url, {
                headers: requestHeaders,
                method: 'POST', // Changed to POST method
                body: requestBody, // Included the request body
            })
                .then((response) => response.json())
                .then((e) => {
                    if (e?.success && e?.data && e?.data?.length > 0) {
                        setMetaField(e?.data?.[0]?.value?.metafield ?? false);
                        setSavedMetaField(e?.data?.[0]?.value?.metafield ?? false);
                    }
                    else {
                        setMetaField(false);
                        setSavedMetaField(false);
                    }
                })
                .catch((error) => {
                    // console.error('Error:', error);
                });
        }
        else {
            setMetaField(false);
            setSavedMetaField(false);
        }
    }

    useEffect(() => {
        setGetLoader(true);
        getProductSettings();
        getLastProductSyncDate();
        getMetaFieldValue();
        return () => {
            if (process.env.REACT_APP_PROD === "1" && !isAdminLoggedIn()) hideSaveBar();
            setErrors({
                fba: false,
                fbm: false
            })
            setFullfillmentError(false);
            setShowContextualSaveBar(false);
        };

    }, []);

    const redirectToChoosePlan = () => {
        props.history('../../home/pricing', {
            replace: false,
            state: {
                plan_name: props.redux.current_plan?.plan_details?.title,
                billed_type: props.redux.current_plan?.plan_details?.billed_type,
                category: props.redux.current_plan?.plan_details?.category
            }
        });
    }

    return <>
        <Modal
            // activator={visi}
            open={metaFieldModal}
            onClose={() => setMetaFieldModal(false)}
            title={t("Meta attribute data synchronization")}
            primaryAction={{
                content: t('Upgrade Plan'),
                onAction: redirectToChoosePlan,
            }}
            secondaryActions={[
                {
                    content: t('Close'),
                    onAction: () => setMetaFieldModal(false),
                }
            ]}
        >
            <Modal.Section>
                <Text as='p'>
                    <Text as='span' fontWeight='semibold'>{t("Please Note:")}</Text> {t("The meta attribute data synchronization feature from Shopify to Amazon is exclusive to paid plans. Access this feature by upgrading to a paid plan.")}
                </Text>
            </Modal.Section>
        </Modal>


        {pageSkeleton ? RenderSkeletonProductDetails() : <>
            {(showContextualSaveBar || (JSON.stringify(savedMetafield) !== JSON.stringify(metafield)) || inventory_fulfillment_latency.saved !== inventory_fulfillment_latency.value || JSON.stringify(fba_priority?.value) !== JSON.stringify(fba_priority?.saved) || JSON.stringify(fbm_priority?.value) !== JSON.stringify(fbm_priority?.saved)) && (process.env.REACT_APP_PROD === "0" ||
                isAdminLoggedIn()) && (
                    <ContextualSaveBar
                        message={t("Unsaved changes")}
                        saveAction={{
                            onAction: () => saveProductSettings(),
                            loading: saveLoader,
                            disabled: false,
                        }}
                        discardAction={{
                            onAction: () => { handleDiscardBtn() },
                        }}
                    />
                )}

            <BlockStack gap="500">
                <Grid columns={{ xs: 1, sm: 1, md: 3, lg: 3, xl: 3 }} >
                    <Grid.Cell columnSpan={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }} >
                        <BlockStack gap="100">
                            <Text variant="headingMd" as="h6" id='product_Text_ProductHeadingDetails'>{t("Product Details")}</ Text>
                            <Text as="p" fontWeight="regular" tone='subdued' id='product_Text_ProductDetailsHeadingDescription'>{t("Effortlessly synchronize all or selective product information from the app to Amazon.")}</Text>
                        </BlockStack>
                    </Grid.Cell>
                </Grid>

                <Divider />

                <Box paddingInlineStart="300" paddingInlineEnd="300" paddingBlockEnd="200" paddingBlockStart="200" background='bg-surface-disabled'>
                    <Text variant="headingSm" as="h6" tone='subdued' id='order_Text_IDENTIFYAMAZONORDERSONSHOPIFY'>
                        {t("SHOPIFY SETTINGS")}
                    </Text>
                </Box>

                <InlineStack gap="200" wrap={false}>
                    <Switch className='switcherBtn' size='small' checked={productSettings.trim_description} onChange={(e) => {
                        onProductSettingsChange("trim_description", "", e)
                    }} disabled={saveLoader || getLoader} />
                    <BlockStack gap="200">
                        <BlockStack gap="100">
                            <div className='Custom__Line--Height'>
                                <Text variant="headingSm" as="h6">
                                    {t("Trim Description")}
                                </Text>
                            </div>
                            <Text as="p" fontWeight="regular" tone='subdued'>
                                {t("Enable this feature to auto-trim product descriptions to fit Amazon’s 2000-character limit. If disabled, any lengthy descriptions will prevent products from uploading successfully.")}
                            </Text>
                            <Text as="p" fontWeight="semibold" tone='subdued'>
                                {t("Note: You can modify the description after the product is uploaded.")}
                            </Text>
                        </BlockStack>
                    </BlockStack>
                </InlineStack>

                <InlineStack gap="200" wrap={false}>
                    <Switch className='switcherBtn' size='small' disabled={shouldMetaAttributesDisabled} checked={metafield} onChange={(e) => {
                        if (!metaFieldSupportAllowed()) {
                            setMetaFieldModal(true);
                        }
                        else {
                            setMetaField(e);
                        }
                    }} />
                    <BlockStack gap="200">
                        <BlockStack gap="100">
                            <div className='Custom__Line--Height'>
                                <Text variant="headingSm" as="h6">
                                    {t("Meta Attributes")}
                                </Text>
                            </div>
                            <Text as="p" fontWeight="regular" tone='subdued'>
                                {t("Enable this feature to import vital meta attribute data from Shopify, enhancing your Amazon product details. Enriched listings will improve customer experience, boost visibility, and drive higher sales.")}
                            </Text>
                        </BlockStack>
                    </BlockStack>
                </InlineStack>

                <InlineStack gap="200" wrap={false}>
                    <Switch className='switcherBtn' size='small' checked={productSettings.product_details} onChange={(e) => { onProductSettingsChange("product_details", "", e) }} disabled={saveLoader || getLoader} />
                    <BlockStack gap="100">
                        <div className='Custom__Line--Height'>
                            <Text variant="headingSm" as="h6" id='product_Text_ProductDetails'>{t("Product Information")}</Text>
                        </div>
                        <div>
                            <Text as="p" fontWeight="regular" tone='subdued' id='product_Text_ProductDetailsDescription'>{t("Choose to sync the crucial product details such as title and description on Amazon from Shopify.")}
                            </Text>
                            <Text as="p" fontWeight="regular" tone='subdued' id='product_Text_ProductDetailsDescription'>
                                {t("Please note: This setting is applicable only for already listed products. When enabled, this will keep your Amazon listings updated.")}
                            </Text>
                        </div>
                    </BlockStack>
                </InlineStack>

                <InlineStack gap="200" wrap={false}>
                    <Switch className='switcherBtn' size='small' checked={productSettings.images}
                        onChange={(e) => { onProductSettingsChange("images", "", e) }} disabled={saveLoader || getLoader} />
                    <BlockStack gap="100">
                        <div className='Custom__Line--Height'>
                            <Text variant="headingSm" as="h6" id='product_Text_ProductImages'>{t("Product Images")}</Text>
                        </div>
                        <div>
                            <Text as="p" fontWeight="regular" tone='subdued' id='product_Text_ProductImagesDescription'>{t("Choose to sync product images on Amazon from Shopify.")}
                            </Text>
                            <Text as="p" fontWeight="regular" tone='subdued' id='product_Text_ProductImagesDescription'>
                                {t("Please note: This setting is applicable only for already listed products. When enabled, this will keep your Amazon listings updated.")}
                            </Text>
                        </div>
                    </BlockStack>
                </InlineStack>

                <InlineStack gap='200' wrap={false}>
                    <Switch className='switcherBtn' size='small' defaultChecked disabled />
                    <BlockStack gap="200">
                        <BlockStack gap="100">
                            <div className='Custom__Line--Height'>
                                <Text variant="headingSm" as="h6">
                                    {t("Set Handling Time")}<span style={{ color: 'red' }}>*</span>
                                </Text>
                            </div>
                            <Text as="p" fontWeight="regular" tone='subdued'>
                                {t("The overall duration it takes to process and ship an order after it's been placed by a customer.")}
                            </Text>
                        </BlockStack>
                        <div className='Custom__TextField--alignmnet'>
                            <TextField
                                label=""
                                suffix={t("days")}
                                value={inventory_fulfillment_latency.value}
                                helpText={t("By default, a 2-day handling time is set. You can customize this to any period between 0 to 30 days.")}
                                autoComplete="off"
                                placeholder={t('Ex: 10')}
                                error={inventory_fulfillment_latency.error && t('Enter a number between 0 to 30')}
                                onChange={(val: any) => {
                                    if (val.match(/[\sa-zA-Z-!$%^&*()@_+|~=`\\#{}\[\]:";'<>?,.\/]/gi)) return;
                                    setInventoryFullfilmentLatency((a: any) => ({
                                        ...a,
                                        value: val,
                                        error: (val == "" || +val < 0) || (+val > 30)
                                    }))
                                }}
                            />
                        </div>
                    </BlockStack >
                </InlineStack >
                <Box paddingInlineStart="300" paddingInlineEnd="300" paddingBlockEnd="200" paddingBlockStart="200" background='bg-surface-disabled'>
                    <Text variant="headingSm" as="h6" tone='subdued' id='order_Text_IDENTIFYAMAZONORDERSONSHOPIFY'>
                        {t("AMAZON SETTINGS")}
                    </Text>
                </Box>

                <InlineStack gap="200" wrap={false}>
                    <Switch className='switcherBtn' size='small' checked={productSettings.sync_feed_process}
                        onChange={(e) => { onProductSettingsChange("sync_feed_process", "", e) }} disabled={saveLoader || getLoader} />
                    <BlockStack gap="100">
                        <div className='Custom__Line--Height'>
                            <Text variant="headingSm" as="h6" id='product_Text_SyncFeedProcess'>{t("Feed Process")}</Text>
                        </div>
                        <div>
                            <Text as="p" fontWeight="regular" tone='subdued' id='product_Text_ProductVariantTitleDescription'>{t("Enable this toggle to automatically send an email notification when an Amazon action completes. The email includes success/error counts, error details (if applicable), and a summary of the results, sent to specified recipients.")}
                            </Text>
                        </div>
                    </BlockStack>
                </InlineStack>

                <InlineStack gap="200" wrap={false}>
                    <Switch className='switcherBtn' size='small' checked={productSettings.variant_title}
                        onChange={(e) => { onProductSettingsChange("variant_title", "", e) }} disabled={saveLoader || getLoader} />
                    <BlockStack gap="100">
                        <div className='Custom__Line--Height'>
                            <Text variant="headingSm" as="h6" id='product_Text_ProductVariantTitle'>{t("Variant Title")}</Text>
                        </div>
                        <div>
                            <Text as="p" fontWeight="regular" tone='subdued' id='product_Text_ProductVariantTitleDescription'>{t("Enable sync to include parent product name with its specific variation in the variant title. For example, if the parent is 'Shirt,' variant titles will be 'Shirt/Blue,' 'Shirt/White,' etc. If not enabled, only the variation ( 'Blue,' 'White') will appear, which may cause confusion for buyers.")}
                            </Text>
                            <Text as="p" fontWeight="semibold" tone='subdued'>
                                {t("Note: Syncing product variant titles allows buyers to see the product’s full context at a glance.")}
                            </Text>
                        </div>
                    </BlockStack>
                </InlineStack>

                {/* My settingg */}
                <InlineStack gap="200" wrap={false}>
                    <Switch className='switcherBtn' size='small' defaultChecked disabled />
                    <BlockStack gap="300">
                        <BlockStack gap="100">
                            <div className='Custom__Line--Height'>
                                <Text variant="headingSm" as="h6">{t("Sync Product By fulfillment-type")}</Text>
                            </div>
                            <BlockStack gap="0">
                                {fullfillmentError && <InlineError message={t("Atleast one setting( i.e FBA or FBM) is required")} fieldID="sync_product_with" />}
                                <BlockStack gap={'050'}>
                                    <Text as="p" fontWeight="regular" tone='subdued'>
                                        {t("Effortlessly sync your Amazon and Shopify products by matching fulfillment methods—Fulfillment by Amazon (FBA) or Fulfillment by Merchant (FBM). For best results, enable both options.")}
                                    </Text>
                                    <Text as="p" fontWeight='bold' tone='success'>{t("Note: At least one method must be selected to proceed.")}</Text>
                                    <Text tone='subdued' as='p'>{t("Matching is also based on SKU or Barcode. You can drag and reorder these two options to set your preferred priority for product matching.")})</Text>
                                </BlockStack>
                                {
                                    (productSettings?.sync_product_with?.['FBA']?.value)
                                        ?
                                        (productSettings?.sync_product_with?.['FBA']?.sku
                                            && productSettings?.sync_product_with?.['FBA']?.barcode)
                                            ?
                                            <Box
                                                padding="100"
                                                background="bg-surface-brand-selected"
                                                borderColor='border-emphasis-hover'
                                                borderWidth='050'
                                                borderRadius="100"
                                            >
                                                <Text as='p'><strong>{t("Note")}:</strong> {t("Set the priority (in case you select both) by the drag and drop option.")}</Text>
                                            </Box>
                                            :
                                            <></>
                                        :
                                        (productSettings?.sync_product_with?.['FBM']?.value)
                                            ?
                                            (productSettings?.sync_product_with?.['FBM']?.sku
                                                && productSettings?.sync_product_with?.['FBM']?.barcode)
                                                ?
                                                <Box
                                                    padding="100"
                                                    background="bg-surface-brand-selected"
                                                    borderColor='border-emphasis-hover'
                                                    borderWidth='050'
                                                    borderRadius="100"
                                                >
                                                    <Text as='p'><strong>{t("Note")}:</strong> {t("Set the priority (in case you select both) by the drag and drop option.")}</Text>
                                                </Box>
                                                :
                                                <></>
                                            :
                                            <></>
                                }
                            </BlockStack >
                        </BlockStack >
                        <BlockStack gap="200">
                            <InlineStack gap="200" wrap={false}>
                                <Switch className='switcherBtn' size='small' checked={productSettings?.sync_product_with?.FBA?.value}
                                    onChange={(e: boolean) => {
                                        let err = !(productSettings?.sync_product_with?.FBM?.value || e);
                                        setFullfillmentError(err);
                                        onProductSettingsChange("sync_product_with", "FBA", e, "value")
                                        if (!e) setErrors({ ...errors, fba: false })
                                        else {
                                            if (!productSettings?.sync_product_with?.FBA.sku && !productSettings?.sync_product_with?.FBA.barcode)
                                                setErrors({ ...errors, fba: true })
                                        }
                                    }} disabled={saveLoader || getLoader} />
                                <div className='Custom__Line--Height'>
                                    <Text fontWeight="regular" as="p">
                                        {t("FBA (Fulfillment by Amazon)")}
                                    </Text>
                                </div>
                            </InlineStack>
                            {
                                productSettings?.sync_product_with?.FBA?.value
                                &&
                                <BlockStack gap="200">
                                    {(!productSettings?.sync_product_with?.FBA?.sku && !productSettings?.sync_product_with?.FBA?.barcode) && <InlineError message={t("Atleast one setting( i.e SKU or Barcode) is required")} fieldID="sync_status_with" />}
                                    {
                                        fba_priority?.value?.length !== 0
                                        &&
                                        <Draggable
                                            type='FBA'
                                            list={fba_priority}
                                            setList={setFbaPriority}
                                            productSettings={productSettings}
                                            setProductSettings={setProductSettings}
                                            errors={errors}
                                            setErrors={setErrors}
                                            onProductSettingsChange={onProductSettingsChange}
                                            saveLoader={saveLoader}
                                            getLoader={getLoader}
                                        />
                                    }
                                </BlockStack>
                            }
                            <InlineStack gap="200" wrap={false}>
                                <Switch className='switcherBtn' size='small' checked={productSettings?.sync_product_with?.FBM?.value}
                                    onChange={(e: boolean) => {
                                        let err = !(productSettings?.sync_product_with?.FBA?.value || e);
                                        setFullfillmentError(err);
                                        onProductSettingsChange("sync_product_with", "FBM", e, "value")
                                        if (!e) setErrors({ ...errors, fbm: false })
                                        else {
                                            if (!productSettings?.sync_product_with?.FBM.sku && !productSettings?.sync_product_with?.FBM.barcode)
                                                setErrors({ ...errors, fbm: true })
                                        }
                                    }} disabled={saveLoader || getLoader} />
                                <div className='Custom__Line--Height'>
                                    <Text fontWeight="regular" as="p">{t("FBM (Fulfillment by Merchant)")}</Text>
                                </div>
                            </InlineStack>
                            {
                                productSettings?.sync_product_with?.FBM?.value
                                &&
                                <BlockStack gap="200">
                                    {(!productSettings?.sync_product_with?.FBM?.sku && !productSettings?.sync_product_with?.FBM?.barcode) && <InlineError message={t("Atleast one setting( i.e SKU or Barcode) is required")} fieldID="sync_status_with" />}
                                    {
                                        fbm_priority?.value?.length !== 0
                                        &&
                                        <Draggable
                                            type='FBM'
                                            list={fbm_priority}
                                            setList={setFbmPriority}
                                            productSettings={productSettings}
                                            setProductSettings={setProductSettings}
                                            errors={errors}
                                            setErrors={setErrors}
                                            onProductSettingsChange={onProductSettingsChange}
                                            saveLoader={saveLoader}
                                            getLoader={getLoader}
                                        />
                                    }
                                </BlockStack>
                            }

                        </BlockStack>
                    </BlockStack >
                </InlineStack >

                {lastSync && <>
                    <Divider />
                    <InlineStack blockAlign='center' align='space-between' gap="200">
                        <div className='Custom__Sync--Update'>
                            <InlineStack gap="200" wrap={false}>
                                <div>
                                    <Icon source={RefreshMajor} tone="base" />
                                </div>
                                <Text variant="headingSm" as="h6" tone='subdued'>
                                    {t("Last updated")}: {lastSync}
                                </Text>
                            </InlineStack>
                        </div >
                    </InlineStack >
                </>}

            </BlockStack >
        </>}
    </>;
}

export default DI(ProductSettings);